import React, { useState } from 'react';
import {
  Button,
  Card,
  Icon,
  Title,
  Dragger,
  UploadFileType,
  UploadChangeParamType,
  message,
} from 'components';
import PhotoUploadModal from './PhotoUploadModal';
import PhotosGrid from './PhotosGrid';
import { PhotoFileType, PhotoFileStatus } from '../../types';
import {
  StyledContainer,
  StyledFlexContainer,
  StyledPhotoLabel,
  StyledPhotosContainer,
  StyledThumbnail,
} from './StyledComponents';

const DRAGGER_TITLE = 'Click or drag photos to this area to upload';

const DRAGGER_DESCRIPTION =
  'After you upload, you are able to select a thumbnail, and reorder your photos.';

const DRAGGER_FILE_FORMATS = ['.jpg', '.jpeg', '.png', '.gif'];

interface PhotosProps {
  photos: PhotoFileType[];
  setPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  setDeletedPhotos: React.Dispatch<React.SetStateAction<PhotoFileType[]>>;
  thumbnail: PhotoFileType | null;
  setThumbnail: React.Dispatch<React.SetStateAction<PhotoFileType | null>>;
  uploading: boolean;
}

const Photos: React.FC<PhotosProps> = ({
  photos,
  setPhotos,
  setDeletedPhotos,
  thumbnail,
  setThumbnail,
  uploading,
}) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const loadPhotos = (newPhotos: PhotoFileType[]) => {
    if (!thumbnail && (photos.length || newPhotos.length)) {
      [...newPhotos, ...photos].every(photo => {
        if (photo.mimetype.includes('image')) {
          setThumbnail(photo);
          return false;
        }
        return true;
      });
    }
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const onUpload = ({
    file,
    fileList,
  }: UploadChangeParamType<UploadFileType<any>>) => {
    setPhotos(() => {
      const newPhotos = fileList.map(_file => {
        const newFile = { ..._file };
        if (
          newFile.response?.status === 'error' ||
          newFile.status === 'error' ||
          newFile?.error?.status >= 400
        ) {
          const errorDetails =
            newFile.response?.message || newFile.response || 'Upload Error';
          message.error(
            `Error uploading file: ${newFile.name}. ${errorDetails}`,
          );
          newFile.status = 'error';
          newFile.response = errorDetails;
        } else if (newFile.status === 'done' && !newFile.response?.url) {
          newFile.status = 'uploading';
        }
        return {
          filename: newFile.name,
          mimetype: newFile.type,
          url: newFile.response?.url,
          status: newFile.status as PhotoFileStatus,
        };
      });

      if (!thumbnail && newPhotos.length) {
        newPhotos.every(photo => {
          if (photo.mimetype.includes('image')) {
            setThumbnail(photo);
            return false;
          }
          return true;
        });
      }
      return newPhotos;
    });
  };

  const uploadPhotoButton = (
    <Button onClick={() => setUploadModalOpen(true)}>
      <Icon type="antdUploadOutlined" /> Upload Photos
    </Button>
  );

  const showGrid = () => photos.length && photos.every(({ url }) => url);

  return (
    <Card
      type="primary"
      title="Photos"
      extra={photos.length ? uploadPhotoButton : null}
      id="photos"
    >
      {showGrid() ? (
        <StyledContainer>
          <StyledFlexContainer>
            <Title level={4} hideMargin>
              Thumbnail
            </Title>
          </StyledFlexContainer>
          <StyledPhotoLabel>
            <Title level={4} hideMargin>
              Photos
            </Title>
          </StyledPhotoLabel>
          <div>
            {!!thumbnail && (
              <StyledThumbnail>
                <img src={thumbnail.url} />
              </StyledThumbnail>
            )}
          </div>
          <StyledPhotosContainer>
            <PhotosGrid
              photos={photos}
              setPhotos={setPhotos}
              setDeletedPhotos={setDeletedPhotos}
              thumbnail={thumbnail}
              setThumbnail={setThumbnail}
            />
          </StyledPhotosContainer>
        </StyledContainer>
      ) : (
        <Dragger
          title={DRAGGER_TITLE}
          description={DRAGGER_DESCRIPTION}
          onChange={onUpload}
          disabled={uploading}
          accept={DRAGGER_FILE_FORMATS.join(',')}
          action="/upload/single"
          openFileDialogOnClick={false}
          showIcon
          multiple
        >
          {uploadPhotoButton}
        </Dragger>
      )}
      <PhotoUploadModal
        visible={uploadModalOpen}
        setVisible={setUploadModalOpen}
        loadPhotos={loadPhotos}
        imagesTypes={DRAGGER_FILE_FORMATS}
      />
    </Card>
  );
};

export default React.memo(Photos);
